import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { getFinanciacionType } from '../../helpers/ofertas-helper';
import { Oferta } from '../../services/ofertas-service/models/ofertas';
import { useTrackingManager } from '../../tracking/use-tracking-manager';

const ImageSliderContainer = styled.section`
	position: relative;
`;

const ImageContainer = styled.div`
	position: relative;
	display: flex;
	overflow-x: scroll;
	white-space: nowrap;
	scroll-snap-type: x mandatory;

	::-webkit-scrollbar {
		display: none; /* for Chrome, Safari and Opera */
		scrollbar-width: none; /* Firefox */
	}
`;

const SliderImage = styled.div<{ backgroundImage: string }>`
	min-width: 100%;
	min-height: 100vh;
	background-image: url('${(props) => props.backgroundImage}');
	object-fit: cover;
	scroll-snap-align: center;
	background-size: cover;
	background-repeat: no-repeat;
	background-position: center;

	@media screen and (max-width: 559px) {
		min-height: 75vh;
	}

	@media screen and (min-width: 560px) and (max-width: 1279px) {
		min-height: 85vh;
	}
`;

const ImageSliderPagination = styled.div`
	position: absolute;
	bottom: 5%;
	left: 50%;
	transform: translateX(-50%);
	display: flex;
	gap: 6px;
	padding: 16px;
	border-radius: 20px;
	background-color: white;
`;

const ImageSliderDot = styled.span`
	display: flex;
	align-items: center;
	justify-content: center;
	height: 6px;
	width: 6px;
	border-radius: 50%;
	cursor: pointer;
	background-color: #96a3a8;
	transition: width 0.4s cubic-bezier(0.23, 1, 0.32, 1);

	&::before {
		opacity: 0;
		content: '';
		display: block;
		height: 6px;
		width: 6px;
		border-radius: 50%;
		transform-origin: 50% 50%;
		transition:
			width 0.6 cubic-bezier(0.23, 1, 0.32, 1),
			transform 0.2s ease-out,
			opacity 0.2s ease-out;
	}

	&.active {
		background-color: #00b0f0;
		width: 24px;
		border-radius: 10px;
		animation: activeDot 0.4s cubic-bezier(0.23, 1, 0.32, 1);
	}

	@keyframes activeDot {
		0%,
		100% {
			transform: scaleY(1);
		}

		30% {
			transform: scaleY(0.75);
		}
	}
`;

export const ImageSlider = ({
	oferta,
}: {
	oferta: Oferta;
}): React.ReactElement => {
	const [currentIndex, setCurrentIndex] = useState<number>(0);

	const sliderRef = useRef<HTMLDivElement>();

	const trackingManager = useTrackingManager();

	const autoScrollInterval = 3000;

	useEffect(() => {
		const handleScroll = () => {
			if (sliderRef.current) {
				const { scrollLeft, offsetWidth } = sliderRef.current;
				const newIndex = Math.round(scrollLeft / offsetWidth);

				setCurrentIndex(newIndex);
			}
		};

		const slider = sliderRef.current;
		if (slider) {
			slider.addEventListener('scroll', handleScroll);
			return () => {
				slider.removeEventListener('scroll', handleScroll);
			};
		}
	}, []);

	useEffect(() => {
		const automaticScroll = () => {
			if (sliderRef.current) {
				const newIndex = (currentIndex + 1) % oferta.galeria.length;

				const { offsetWidth } = sliderRef.current;
				sliderRef.current.scrollTo({
					left: newIndex * offsetWidth,
					behavior: 'smooth',
				});

				setCurrentIndex(newIndex);
			}
		};

		const scrollTimer = setInterval(automaticScroll, autoScrollInterval);

		return () => {
			clearInterval(scrollTimer);
		};
	}, [currentIndex, oferta.galeria.length]);

	const selectImage = (index: number) => {
		if (sliderRef.current) {
			const { offsetWidth } = sliderRef.current;
			sliderRef.current.scrollTo({
				left: index * offsetWidth,
				behavior: 'smooth',
			});

			trackingManager.trackOfertasButtonClick(
				`model-image-${index + 1}`,
				{
					contentId: `model-image-${index + 1}`,
				},
				{
					CarlineId: oferta.carline?.code.toString(),
					CarlineName: oferta.carline?.title,
					ModelId: oferta.motorizacionCode?.split('-')[0],
					ModelName: oferta.carline?.salesgroup.model.name,
					ModelYear: oferta.carline?.salesgroup.year.toString(),
					SalesgroupName: oferta.carline?.salesgroup.title,
					PriceRateType: getFinanciacionType(oferta),
					PriceRateTotal: oferta?.cuota ?? undefined,
					PriceRateCurrency: 'EUR',
					PriceTotalTotal: oferta?.precio ?? undefined,
					PriceTotalCurrency: 'EUR',
				},
			);

			setCurrentIndex(index);
		}
	};

	return (
		<ImageSliderContainer className="image-slider">
			<ImageContainer ref={sliderRef} className="image-container">
				{oferta.galeria &&
					oferta.galeria.map((image) => (
						<SliderImage className="slider-image" backgroundImage={image} />
					))}
			</ImageContainer>
			{oferta.galeria && (
				<ImageSliderPagination className="image-slider__pagination">
					{oferta.galeria.map((key, index) => (
						<ImageSliderDot
							className={`image-slider__dot ${index === currentIndex && 'active'}`}
							key={key}
							onClick={() => selectImage(index)}
						/>
					))}
				</ImageSliderPagination>
			)}
		</ImageSliderContainer>
	);
};
